import React, { useState, useEffect, useContext } from "react";
import {
  Typography,
  Box,
  Paper,
  Card,
  CardActionArea,
  Avatar,
  Divider,
  Chip,
  useTheme,
  IconButton,
  Tooltip,
  Badge,
  Stack,
} from "@mui/material";
import Q8Breadcrumbs from "../components/Q8Breadcrumbs";
import { useNavigate } from "react-router-dom";
import Q8Button from "../components/Q8Button";
import OpexService from "../service/opexService";
import AfeService from "../service/afeService";
import {
  Dashboard as DashboardIcon,
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon,
  SupervisedUserCircle as UserIcon,
  ViewList as ListIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { UserContext } from "../context/UserContext.js";
import ImportService from "../service/importService";

const Homepage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, bestRole } = useContext(UserContext);

  // Stati
  const [opexData, setOpexData] = useState([]);
  const [opexAllocationData, setOpexAllocationData] = useState([]);
  const [afeData, setAfeData] = useState([]);
  const [pendingOpexChanges, setPendingOpexChanges] = useState([]);
  const [pendingOpexAllocationChanges, setPendingOpexAllocationChanges] = useState([]);
  const [pendingAfeChanges, setPendingAfeChanges] = useState([]);
  const [error, setError] = useState(null);
  const [lastImportDate, setLastImportDate] = useState(null);

  // Helper function to extract changes from record level data
  const extractChangesFromRecords = (changes) => {
    if (!changes || changes.length === 0) return [];

    return changes
      .map((change) => {
        try {
          // Parse JSON strings to objects if they're not already parsed
          const originalRecord =
            typeof change.originalRecordJson === "string"
              ? JSON.parse(change.originalRecordJson)
              : change.originalRecord || {};

          const modifiedRecord =
            typeof change.modifiedRecordJson === "string"
              ? JSON.parse(change.modifiedRecordJson)
              : change.modifiedRecord || {};

          // Determine which fields have changed
          const changedFields = [];
          Object.keys(modifiedRecord).forEach((key) => {
            // Skip internal fields or null values
            if (
              key === "id" ||
              key === "original_Id" ||
              key === "hasPendingChanges" ||
              key === "pendingChangeBy" ||
              originalRecord[key] === undefined
            ) {
              return;
            }

            // Check if values are different
            if (
              JSON.stringify(originalRecord[key]) !==
              JSON.stringify(modifiedRecord[key])
            ) {
              changedFields.push({
                field: key,
                oldValue:
                  originalRecord[key] !== null
                    ? originalRecord[key].toString()
                    : "null",
                newValue:
                  modifiedRecord[key] !== null
                    ? modifiedRecord[key].toString()
                    : "null",
              });
            }
          });

          // For OPEX changes
          if (change.contract || modifiedRecord.contract) {
            return {
              id: change.id,
              contract: change.contract || modifiedRecord.contract || "N/A",
              field:
                changedFields.length > 0 ? changedFields[0].field : "Multiple",
              oldValue:
                changedFields.length > 0 ? changedFields[0].oldValue : "N/A",
              newValue:
                changedFields.length > 0 ? changedFields[0].newValue : "N/A",
              modifiedBy: change.modifiedBy,
              modifiedAt: change.modifiedAt,
              // Keep track of all changes for potential detailed view
              allChanges: changedFields,
            };
          }
          // For AFE/CAPEX changes
          else {
            return {
              id: change.id,
              afeCode: change.afeCode || modifiedRecord.afeCode || "N/A",
              field:
                changedFields.length > 0 ? changedFields[0].field : "Multiple",
              oldValue:
                changedFields.length > 0 ? changedFields[0].oldValue : "N/A",
              newValue:
                changedFields.length > 0 ? changedFields[0].newValue : "N/A",
              modifiedBy: change.modifiedBy,
              modifiedAt: change.modifiedAt,
              // Keep track of all changes for potential detailed view
              allChanges: changedFields,
            };
          }
        } catch (err) {
          console.error("Error processing change:", err, change);
          // Return a basic structure if there's an error
          return {
            id: change.id,
            afeCode: change.afeCode || "Error",
            contract: change.contract || "Error",
            field: "Error processing",
            oldValue: "Error",
            newValue: "Error",
            modifiedBy: change.modifiedBy || "Unknown",
            modifiedAt: change.modifiedAt,
          };
        }
      })
      .filter((change) => change != null);
  };

  const fetchLastImportDate = async () => {
    try {
      const response = await ImportService.getLastImportDate();
      if (response && response.lastImportDate) {
        const date = new Date(response.lastImportDate);
        setLastImportDate(date);
      }
    } catch (error) {
      console.error(
        "Errore durante il recupero della data di ultima importazione:",
        error
      );
    }
  };

  // Fetch all data
  const fetchAllData = async () => {
    setError(null);

    try {
      await fetchLastImportDate();
      // Get the current fiscal year

      // Load OPEX data
      const opexResponse = await OpexService.getOpexes(false);
      setOpexData(opexResponse);
      const opexAllocationResponse = await OpexService.getOpexes(true);
      setOpexAllocationData(opexAllocationResponse);
      // Load AFE/CAPEX data
      const afeResponse = await AfeService.getAfes();
      setAfeData(afeResponse);

      // Load pending changes only if user is Admin or Validator
      if (bestRole && (bestRole === "Admin" || bestRole === "Validator")) {
        try {
          // Use getPendingRecordChanges() to match the method used in table components
          const opexChanges = await OpexService.getPendingRecordChanges(false);
          const opexAllocationChanges = await OpexService.getPendingRecordChanges(true);

          // Filter only pending status changes
          const pendingOpexChanges = opexChanges.filter(
            (change) => change.status === "pending"
          );
          const pendingOpexAllocationChanges = opexAllocationChanges.filter(
            (change) => change.status === "pending"
          );
          // Transform the data to the format expected by the UI
          const transformedOpexChanges = extractChangesFromRecords(pendingOpexChanges);
          setPendingOpexChanges(transformedOpexChanges || []);
          const transformedOpexAllocationChanges = extractChangesFromRecords(pendingOpexAllocationChanges);
          setPendingOpexAllocationChanges(transformedOpexAllocationChanges || []);
        } catch (err) {
          console.error("Error fetching OPEX pending changes:", err);
          setPendingOpexChanges([]);
        }

        try {
          // Use getPendingRecordChanges() to match the method used in table components
          const afeChanges = await AfeService.getPendingRecordChanges();

          // Filter only pending status changes
          const pendingAfeChanges = afeChanges.filter(
            (change) => change.status === "pending"
          );
          // Transform the data to the format expected by the UI
          const transformedAfeChanges =
            extractChangesFromRecords(pendingAfeChanges);
          setPendingAfeChanges(transformedAfeChanges || []);
        } catch (err) {
          console.error("Error fetching CAPEX pending changes:", err);
          setPendingAfeChanges([]);
        }
      } else {
        // Reset pending changes if the user doesn't have permissions
        setPendingOpexChanges([]);
        setPendingOpexAllocationChanges([]);
        setPendingAfeChanges([]);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError(
        "Si è verificato un errore durante il caricamento dei dati. Riprova più tardi."
      );
    }
  };

  // Initial data load
  useEffect(() => {
    if(bestRole){
      fetchAllData();
    }
  }, [bestRole]); // Refetch when bestRole changes

  // Handle refresh
  const handleRefresh = async () => {
    await fetchAllData();
  };

  // Only show modules the user has access to based on their role
  const getModules = () => {
    const modules = [
      {
        id: 1,
        title: "Tabella OPEX",
        icon: <ListIcon />,
        color: theme.palette.primary.main,
        description: "Gestione dati OPEX",
        route: "/opex",
        badge: pendingOpexChanges.length,
        access: true, // Everyone has access to this
      },
      {
        id: 1,
        title: "Tabella OPEX Allocation",
        icon: <ListIcon />,
        color: theme.palette.primary.main,
        description: "Gestione dati OPEX Allocation",
        route: "/opex-allocation",
        badge: pendingOpexAllocationChanges.length,
        access: true, // Everyone has access to this
      },
      {
        id: 2,
        title: "Tabella CAPEX",
        icon: <WalletIcon />,
        color: theme.palette.secondary.main,
        description: "Gestione dati CAPEX",
        route: "/capex",
        badge: pendingAfeChanges.length,
        access: true, // Everyone has access to this
      },
      {
        id: 3,
        title: "Utenti",
        icon: <UserIcon />,
        color: "#FF9800",
        description: "Gestione degli utenti",
        route: "/usermanagement",
        badge: 0,
        // Only Admin has access to User Management
        access: bestRole === "Admin",
      },
    ];

    // Return only modules the user has access to
    return modules.filter((module) => module.access);
  };

  // Rendering dell'utente in header
  const renderUserInfo = () => {
    if (user?.name) {
      return `Benvenuto, ${user.name}`;
    }
    return "Benvenuto nel sistema di gestione";
  };

  // Rendering contenuto pendenti
  const renderPendingChangesContent = () => {
    // Se non ci sono modifiche pendenti
    if (pendingOpexChanges.length === 0 && pendingAfeChanges.length === 0 && pendingOpexAllocationChanges.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: theme.palette.success.light,
              width: 60,
              height: 60,
            }}
          >
            <DashboardIcon sx={{ fontSize: 30 }} />
          </Avatar>
          <Typography variant="body1" align="center">
            Non ci sono modifiche in attesa di approvazione
          </Typography>
        </Box>
      );
    }

    // Altrimenti mostra le modifiche pendenti
    return (
      <>
        {/* OPEX Pending Changes */}
        {pendingOpexChanges.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 2 }}>
              OPEX ({pendingOpexChanges.length})
            </Typography>

            <Stack spacing={1}>
              {pendingOpexChanges.slice(0, 5).map((change, index) => (
                <Box key={change.id || index} sx={{ py: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Contract: {change.contract}
                    </Typography>
                    <Chip
                      label="In attesa"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2">
                    <strong>
                      Modifiche al record in attesa di approvazione
                    </strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Proposto da: {change.modifiedBy} •{" "}
                    {new Date(change.modifiedAt).toLocaleDateString()}
                  </Typography>
                  {index < Math.min(pendingOpexChanges.length, 5) - 1 && (
                    <Divider sx={{ mt: 1 }} />
                  )}
                </Box>
              ))}
            </Stack>

            {pendingOpexChanges.length > 5 && (
              <Typography
                variant="body2"
                color="primary"
                align="center"
                sx={{ mt: 1, cursor: "pointer" }}
                onClick={() => navigate("/opex")}
              >
                + {pendingOpexChanges.length - 5} altre modifiche
              </Typography>
            )}
          </>
        )}
        {pendingOpexAllocationChanges.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 2 }}>
              OPEX Allocation ({pendingOpexAllocationChanges.length})
            </Typography>

            <Stack spacing={1}>
              {pendingOpexAllocationChanges.slice(0, 5).map((change, index) => (
                <Box key={change.id || index} sx={{ py: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      Contract: {change.contract}
                    </Typography>
                    <Chip
                      label="In attesa"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2">
                    <strong>
                      Modifiche al record in attesa di approvazione
                    </strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Proposto da: {change.modifiedBy} •{" "}
                    {new Date(change.modifiedAt).toLocaleDateString()}
                  </Typography>
                  {index < Math.min(pendingOpexAllocationChanges.length, 5) - 1 && (
                    <Divider sx={{ mt: 1 }} />
                  )}
                </Box>
              ))}
            </Stack>

            {pendingOpexAllocationChanges.length > 5 && (
              <Typography
                variant="body2"
                color="primary"
                align="center"
                sx={{ mt: 1, cursor: "pointer" }}
                onClick={() => navigate("/opex")}
              >
                + {pendingOpexAllocationChanges.length - 5} altre modifiche
              </Typography>
            )}
          </>
        )}

        {/* CAPEX Pending Changes */}
        {pendingAfeChanges.length > 0 && (
          <>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 3 }}>
              CAPEX ({pendingAfeChanges.length})
            </Typography>

            <Stack spacing={1}>
              {pendingAfeChanges.slice(0, 5).map((change, index) => (
                <Box key={change.id || index} sx={{ py: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      AFE: {change.afeCode}
                    </Typography>
                    <Chip
                      label="In attesa"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2">
                    <strong>
                      Modifiche al record in attesa di approvazione
                    </strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Proposto da: {change.modifiedBy} •{" "}
                    {new Date(change.modifiedAt).toLocaleDateString()}
                  </Typography>
                  {index < Math.min(pendingAfeChanges.length, 5) - 1 && (
                    <Divider sx={{ mt: 1 }} />
                  )}
                </Box>
              ))}
            </Stack>

            {pendingAfeChanges.length > 5 && (
              <Typography
                variant="body2"
                color="primary"
                align="center"
                sx={{ mt: 1, cursor: "pointer" }}
                onClick={() => navigate("/capex")}
              >
                + {pendingAfeChanges.length - 5} altre modifiche
              </Typography>
            )}
          </>
        )}

        {/* View All Button */}
        {(bestRole === "Admin" || bestRole === "Validator") && (
          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}></Box>
        )}
      </>
    );
  };

  // Get actual modules based on user role
  const modules = getModules();

  const getUserRoleToDisplay = (role) => {
    switch (role) {
      case "Admin":
        return "Amministratore";

      case "Validator":
        return "Validatore";

      case "Coordinator":
        return "Coordinatore";
    }
  };

  return (
    <Box
      className="homepage-container"
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, rgba(30,40,50,1) 0%, rgba(50,60,70,1) 100%)"
        //     : "linear-gradient(135deg, #f5f7fa 0%, #e4ecfb 100%)",
        p: { xs: 2, md: 3 },
      }}
    >
      <Q8Breadcrumbs links={[]} variant="primary" />

      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
          mt: 2,
          flexWrap: { xs: "wrap", md: "nowrap" },
          gap: 2,
          pb: 10,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              color: theme.palette.primary.main,
              fontWeight: 600,
              mb: 1,
            }}
          >
            Dashboard Q8
          </Typography>
          <Typography variant="h6" sx={{ color: "text.secondary" }}>
            {renderUserInfo()}
            {user?.userRoles?.length > 0 && (
              <Chip
                size="small"
                label={getUserRoleToDisplay(user.userRoles[0].roleName)}
                color="primary"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
        </Box>

        <Box>
          <Tooltip title="Aggiorna dati">
            <IconButton onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Error message if any */}
      {error && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: "#FFF4E5", color: "#663C00" }}>
          <Typography>{error}</Typography>
        </Paper>
      )}

      {/* Quick Stats */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr", // Full width on mobile
              sm: "repeat(2, 1fr)", // 2 columns on small screens
              md: "repeat(3, 1fr)", // 3 columns on medium and up
            },
            gap: 3,
          }}
        >
          {/* OPEX Records Card */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
              transition: "all 0.3s",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "4px",
                bgcolor: theme.palette.primary.main,
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                <ReceiptIcon />
              </Avatar>
              <Typography variant="h6" component="div">
                OPEX Records
              </Typography>
            </Box>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: "bold", mb: 1 }}
            >
              {opexData.length.toLocaleString()}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <b>
                {(() => {
                  // Inizia con il conteggio di base dal dataset OPEX
                  let countWithLevel1 = opexData.filter(
                    (od) => od.raggruppamento1 !== "" && od.raggruppamento1
                  ).length;

                  // Aggiungi i record con modifiche pendenti che valorizzano Livello 1
                  if (pendingOpexChanges.length > 0) {
                    // Ottieni gli ID dei record che già hanno Livello 1 valorizzato
                    const existingLevel1Ids = new Set(
                      opexData
                        .filter(
                          (od) =>
                            od.raggruppamento1 !== "" && od.raggruppamento1
                        )
                        .map((od) => od.id)
                    );

                    // Conta le modifiche pendenti che valorizzano Livello 1 per record che non lo hanno già
                    pendingOpexChanges.forEach((change) => {
                      // Controlla se la modifica include il campo Livello 1
                      const hasLevel1Change = change.allChanges?.some(
                        (c) =>
                          (c.field === "raggruppamento1" ||
                            c.field === "liv1") &&
                          c.newValue &&
                          c.newValue !== "null" &&
                          c.newValue !== ""
                      );

                      // Se la modifica valorizza Livello 1 e il record non era già contato
                      if (
                        hasLevel1Change &&
                        !existingLevel1Ids.has(change.id)
                      ) {
                        countWithLevel1++;
                        // Aggiungi alla Set per evitare doppi conteggi
                        existingLevel1Ids.add(change.id);
                      }
                    });
                  }

                  return countWithLevel1.toLocaleString();
                })()}{" "}
                Record(s)
              </b>{" "}
              su <b>{opexData.length.toLocaleString()}</b> hanno il Livello 1
              valorizzato.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1.5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box>
                  <b>Data aggiornamento Data Platform</b>:{" "}
                  {(() => {
                    const latestUpdate = opexData.reduce((latest, current) => {
                      return new Date(latest?.dataSourceLastUpdate) >
                        new Date(current?.dataSourceLastUpdate)
                        ? latest
                        : current;
                    }, opexData[0])?.dataSourceLastUpdate;

                    return latestUpdate
                      ? new Date(latestUpdate).toLocaleDateString("it-IT")
                      : "";
                  })()}
                </Box>
                <Box>
                  <b>Data aggiornamento procedura Web App</b>:{" "}
                  {lastImportDate
                    ? lastImportDate.toLocaleDateString("it-IT", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : ""}
                </Box>
              </Box>
            </Typography>
          </Paper>

          {/* OPEX Allocation Records Card */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
              transition: "all 0.3s",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "4px",
                bgcolor: theme.palette.primary.main,
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.primary.main, mr: 2 }}>
                <ReceiptIcon />
              </Avatar>
              <Typography variant="h6" component="div">
                OPEX Allocation Records
              </Typography>
            </Box>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: "bold", mb: 1 }}
            >
              {opexAllocationData.length.toLocaleString()}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <b>
                {(() => {
                  // Inizia con il conteggio di base dal dataset OPEX
                  let countWithLevel1 = opexAllocationData.filter(
                    (od) => od.raggruppamento1 !== "" && od.raggruppamento1
                  ).length;

                  // Aggiungi i record con modifiche pendenti che valorizzano Livello 1
                  if (pendingOpexAllocationChanges.length > 0) {
                    // Ottieni gli ID dei record che già hanno Livello 1 valorizzato
                    const existingLevel1Ids = new Set(
                      opexAllocationData
                        .filter(
                          (od) =>
                            od.raggruppamento1 !== "" && od.raggruppamento1
                        )
                        .map((od) => od.id)
                    );

                    // Conta le modifiche pendenti che valorizzano Livello 1 per record che non lo hanno già
                    pendingOpexAllocationChanges.forEach((change) => {
                      // Controlla se la modifica include il campo Livello 1
                      const hasLevel1Change = change.allChanges?.some(
                        (c) =>
                          (c.field === "raggruppamento1" ||
                            c.field === "liv1") &&
                          c.newValue &&
                          c.newValue !== "null" &&
                          c.newValue !== ""
                      );

                      // Se la modifica valorizza Livello 1 e il record non era già contato
                      if (
                        hasLevel1Change &&
                        !existingLevel1Ids.has(change.id)
                      ) {
                        countWithLevel1++;
                        // Aggiungi alla Set per evitare doppi conteggi
                        existingLevel1Ids.add(change.id);
                      }
                    });
                  }

                  return countWithLevel1.toLocaleString();
                })()}{" "}
                Record(s)
              </b>{" "}
              su <b>{opexAllocationData.length.toLocaleString()}</b> hanno il Livello 1
              valorizzato.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1.5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box>
                  <b>Data aggiornamento Data Platform</b>:{" "}
                  {(() => {
                    const latestUpdate = opexAllocationData.reduce((latest, current) => {
                      return new Date(latest?.dataSourceLastUpdate) >
                        new Date(current?.dataSourceLastUpdate)
                        ? latest
                        : current;
                    }, opexData[0])?.dataSourceLastUpdate;

                    return latestUpdate
                      ? new Date(latestUpdate).toLocaleDateString("it-IT")
                      : "";
                  })()}
                </Box>
                <Box>
                  <b>Data aggiornamento procedura Web App</b>:{" "}
                  {lastImportDate
                    ? lastImportDate.toLocaleDateString("it-IT", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : ""}
                </Box>
              </Box>
            </Typography>
          </Paper>

          {/* CAPEX Records Card */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
              transition: "all 0.3s",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "4px",
                bgcolor: theme.palette.secondary.main,
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.secondary.main, mr: 2 }}>
                <WalletIcon />
              </Avatar>
              <Typography variant="h6" component="div">
                CAPEX Records
              </Typography>
            </Box>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: "bold", mb: 1 }}
            >
              {afeData.length.toLocaleString()}
            </Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              <b>
                {(() => {
                  // Inizia con il conteggio di base dal dataset CAPEX/AFE
                  let countWithLevel1 = afeData.filter(
                    (od) => od.raggruppamento1 !== "" && od.raggruppamento1
                  ).length;

                  // Aggiungi i record con modifiche pendenti che valorizzano Livello 1
                  if (pendingAfeChanges.length > 0) {
                    // Ottieni gli ID dei record che già hanno Livello 1 valorizzato
                    const existingLevel1Ids = new Set(
                      afeData
                        .filter(
                          (od) =>
                            od.raggruppamento1 !== "" && od.raggruppamento1
                        )
                        .map((od) => od.id)
                    );

                    // Conta le modifiche pendenti che valorizzano Livello 1 per record che non lo hanno già
                    pendingAfeChanges.forEach((change) => {
                      // Controlla se la modifica include il campo Livello 1
                      const hasLevel1Change = change.allChanges?.some(
                        (c) =>
                          (c.field === "raggruppamento1" ||
                            c.field === "liv1") &&
                          c.newValue &&
                          c.newValue !== "null" &&
                          c.newValue !== ""
                      );

                      // Se la modifica valorizza Livello 1 e il record non era già contato
                      if (
                        hasLevel1Change &&
                        !existingLevel1Ids.has(change.id)
                      ) {
                        countWithLevel1++;
                        // Aggiungi alla Set per evitare doppi conteggi
                        existingLevel1Ids.add(change.id);
                      }
                    });
                  }

                  return countWithLevel1.toLocaleString();
                })()}{" "}
                Record(s)
              </b>{" "}
              su <b>{afeData.length.toLocaleString()}</b> hanno il Livello 1
              valorizzato.
            </Typography>
            <Typography variant="body2" component="div" sx={{ mt: 1.5 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Box>
                  <b>Data aggiornamento Data Platform</b>:{" "}
                  {(() => {
                    const latestUpdate = afeData.reduce((latest, current) => {
                      return new Date(latest?.dataSourceLastUpdate) >
                        new Date(current?.dataSourceLastUpdate)
                        ? latest
                        : current;
                    }, afeData[0])?.dataSourceLastUpdate;

                    return latestUpdate
                      ? new Date(latestUpdate).toLocaleDateString("it-IT")
                      : "";
                  })()}
                </Box>
                <Box>
                  <b>Data aggiornamento procedura Web App</b>:{" "}
                  {lastImportDate
                    ? lastImportDate.toLocaleDateString("it-IT", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : ""}
                </Box>
              </Box>
            </Typography>
          </Paper>
          <Paper
            elevation={2}
            sx={{
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
            }}
          >
            <Box sx={{mb: 9}}  />
          </Paper>       
          <Paper
            elevation={2}
            sx={{
              backgroundColor: "transparent",
              border: "none",
              boxShadow: "none",
            }}
          >
            <Box sx={{mb: 9}}  />
          </Paper>       
          {/* Pending Changes Card */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              overflow: "hidden",
              position: "relative",
              transition: "all 0.3s",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "4px",
                bgcolor: "#F44336",
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Avatar sx={{ bgcolor: "#F44336", mr: 2 }}>
                <NotificationsIcon />
              </Avatar>
              <Typography variant="h6" component="div">
                Modifiche pendenti
              </Typography>
            </Box>
            <Typography
              variant="h4"
              component="div"
              sx={{ fontWeight: "bold", mb: 1 }}
            >
              {(
                pendingOpexChanges.length + pendingAfeChanges.length + pendingOpexAllocationChanges.length
              ).toString()}
            </Typography>
          </Paper>
        </Box>
      </Box>

      {/* Main Content and Navigation */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        {/* Left Column - Module Navigation */}
        <Box sx={{ flex: { xs: "1 1 100%", md: "2 1 0%" } }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              height: "100%",
              minHeight: "400px",
            }}
          >
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Moduli del Sistema
            </Typography>

            {
              <>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
                  {modules.map((module) => (
                    <Card
                      key={module.id}
                      sx={{
                        height: "100%",
                        transition: "all 0.2s",
                        flexBasis: { xs: "100%", sm: "calc(50% - 12px)" },
                        flexGrow: 1,
                        "&:hover": {
                          transform: "scale(1.02)",
                          boxShadow: theme.shadows[8],
                        },
                        position: "relative",
                      }}
                    >
                      {module.badge > 0 && (
                        <Badge
                          badgeContent={module.badge}
                          color="error"
                          sx={{
                            position: "absolute",
                            top: 12,
                            margin: 3,
                            right: 12,
                            zIndex: 1,
                          }}
                        />
                      )}
                      <CardActionArea
                        onClick={() => navigate(module.route)}
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          p: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: module.color,
                              width: 48,
                              height: 48,
                              mr: 2,
                            }}
                          >
                            {module.icon}
                          </Avatar>
                          <Box>
                            <Typography
                              variant="h6"
                              component="div"
                              sx={{ fontWeight: "bold" }}
                            >
                              {module.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {module.description}
                            </Typography>
                          </Box>
                        </Box>
                      </CardActionArea>
                    </Card>
                  ))}
                </Box>

                {/* Shortcut Actions */}
                <Box
                  sx={{
                    mt: 4,
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Q8Button
                    variant="primary"
                    label="Visualizza OPEX"
                    onClick={() => navigate("/opex")}
                    startIcon={<ListIcon />}
                    size="medium"
                  />
                  <Q8Button
                    variant="primary"
                    label="Visualizza OPEX Allocation"
                    onClick={() => navigate("/opex-allocation")}
                    startIcon={<ListIcon />}
                    size="medium"
                  />
                  <Q8Button
                    variant="secondary"
                    label="Visualizza CAPEX"
                    onClick={() => navigate("/capex")}
                    startIcon={<WalletIcon />}
                    size="medium"
                  />
                </Box>
              </>
            }
          </Paper>
        </Box>

        {/* Right Column - Pending Changes Overview */}
        <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 0%" } }}>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              borderRadius: 2,
              height: "100%",
              minHeight: "400px",
            }}
          >
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
              Modifiche Pendenti
            </Typography>

            {renderPendingChangesContent()}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default Homepage;
